import type { JSX } from 'react'
import { Link, useParams } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { formatDateTime, formatDayMonth, initials } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { CONTACT_LABELS, IST, STAGE_LABELS, STAGE_TONES, formatScore, postAge } from './stages'

type Activity = Schema<'ActivityOut'>
type Contact = Schema<'ContactOut'>

/** `FOLLOW_UP_CANCEL_REASONS`, said the way a tenant would say it. */
const CANCEL_REASONS: Record<string, string> = {
  replied: 'they replied',
  suppressed: 'they asked us to stop',
  won: 'you marked them won',
  lost: 'you marked them lost',
  ladder_changed: 'you changed the ladder',
  sending_paused: 'you switched sending off',
  no_address: 'we have no address for them on that channel',
  invalid_address: 'their address cannot be delivered to — check it and re-enter it',
}

/** `ReplyCategory` in `app/v1/inbox.py`, in the tenant's words. */
const REPLY_CATEGORIES: Record<string, string> = {
  interested: 'interested',
  not_interested: 'not interested',
  question: 'a question',
  auto_reply: 'an automatic reply',
  wrong_person: 'the wrong person',
}

/** Which contact kinds go on the suppression list as which kind. Identical
 *  vocabularies (`CONTACT_KINDS` and `SUPPRESSION_KINDS` both carry these three),
 *  written out rather than passed through, because a kind the suppression list
 *  does not admit must fail here and not at the API. */
const SUPPRESSION_KIND: Record<string, 'email' | 'mobile' | 'linkedin'> = {
  email: 'email',
  mobile: 'mobile',
  linkedin: 'linkedin',
}

/**
 * One lead — `design/screens/outreach.html#lead`, audit row §B2.
 *
 * The prototype's own argument for this screen is *"the score is explained by
 * its components. 'Why this lead' is what makes an automated list trustworthy —
 * without it people assume the ranking is arbitrary and ignore it."*
 *
 * WHAT THE SCORE PANEL SHOWS, AND WHY IT IS NOT THE PROTOTYPE'S FIVE ROWS
 * ------------------------------------------------------------------------
 * The prototype drew a component breakdown — *Title match +34*, *Company size
 * 500+ +22* — and there is no such thing anywhere in the product. `leads` carries
 * `relevance_score` and `relevance_reason`: one number and the model's own
 * sentence about why (`app/outreach/ai.Relevance`, whose docstring says the
 * reason is kept precisely because *"why was this a lead is the first question
 * anybody asks about a bad one"*).
 *
 * Rendering five invented components would be exactly the failure the prototype
 * is guarding against — a breakdown that looks measured and is fabricated. So
 * this shows the score and the model's reason, which is the true version of the
 * same promise, and says which model produced it. OUTR-27 settled it in the
 * other direction too: the mockup now draws the reason, so the argument does not
 * have to be had again.
 *
 * **The ICP sentence is what it was measured against**, and the screen says so
 * with a link: that is the loop `#compose`'s own help text describes, and until
 * S-B20 nothing on either end of it existed.
 *
 * WHAT THE POST BADGE CAN SAY ABOUT WHEN THE POST WAS WRITTEN
 * -------------------------------------------------------------
 * OUTR-24 asks for *"12 Jul · 6 hours old when found"*. A post-search page states
 * a **relative** age and never a date, so the two true facts are the moment the
 * search ran — `first_seen_at`, exact — and LinkedIn's own words for how old the
 * post was at that moment. The badge shows both and labels which is which.
 * Subtracting one from the other to mint a post date would turn *"3w"* into a
 * timestamp accurate to the second.
 */
export function LeadDetail(): JSX.Element {
  const brand = useBrand()
  const { leadId = '' } = useParams()
  const { membership } = useActiveTenant()
  const manageable = may(membership, 'outreach:manage')
  const toast = useToast()

  const detail = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/outreach/leads/{lead_id}', {
          params: { path: { lead_id: leadId } },
          signal,
        }),
      ),
    [leadId],
  )

  const move = useMutation((stage: string) =>
    call(() =>
      api.POST('/api/v1/outreach/leads/{lead_id}/stage', {
        params: { path: { lead_id: leadId } },
        body: { stage },
      }),
    ),
  )

  // The button behind an unscored lead. The live tenant's first three leads
  // carried `ai-error` from 2026-09-12 — a batch the model refused, fixed the
  // same day — with nothing on any screen that could ask again.
  const rescore = useMutation(() =>
    call(() =>
      api.POST('/api/v1/outreach/leads/{lead_id}/score', {
        params: { path: { lead_id: leadId } },
      }),
    ),
  )

  const suppress = useMutation((contact: Contact) =>
    call(() =>
      api.POST('/api/v1/outreach/suppression', {
        body: {
          kind: SUPPRESSION_KIND[contact.kind] ?? 'email',
          value: contact.value,
          reason: 'manual',
          detail: 'blocked from the lead screen',
        },
      }),
    ),
  )

  if (detail.state.status === 'loading') return <Loading label="Loading this lead" lines={8} />
  if (detail.state.status === 'error')
    return (
      <div className="ar-stack ar-stack--loose">
        <h1 className="ar-h1">Lead</h1>
        <ErrorState
          failure={detail.state.failure}
          onRetry={detail.refetch}
          title="Could not load this lead"
        />
      </div>
    )

  const data = detail.state.data
  const { lead, contacts, activity } = data
  const blockable = contacts.filter((contact) => contact.kind in SUPPRESSION_KIND)

  const onMove = (stage: string): void => {
    void (async () => {
      const result = await move.run(stage)
      if (result.ok) {
        toast.show({
          kind: 'success',
          title: `Moved to ${STAGE_LABELS[stage] ?? stage}`,
          body:
            result.data.follow_ups_cancelled > 0
              ? 'Their follow-ups have stopped.'
              : 'Nothing was scheduled for them.',
        })
        detail.refetch()
      }
    })()
  }

  /**
   * OUTR-28 — every address this lead holds, on the list, in one press.
   *
   * **Every address, not the first.** The list is keyed by address, so blocking
   * an email and leaving a mobile behind is a lead who keeps hearing from you on
   * WhatsApp after somebody pressed the button that says they will not. The
   * addresses are walked in series rather than raced: the API audits each add,
   * and a burst of parallel writes on the same tenant is a lock-ordering problem
   * bought for nothing on a list this short.
   */
  const onSuppress = (): void => {
    void (async () => {
      let added = 0
      for (const contact of blockable) {
        const result = await suppress.run(contact)
        if (!result.ok) return
        if (result.data.created) added += 1
      }
      toast.show({
        kind: 'success',
        title: added === 0 ? 'Already on your suppression list' : 'Added to your suppression list',
        body:
          added === 0
            ? 'Nothing was sent to them and nothing will be.'
            : `Nothing will be sent to ${added === 1 ? 'this address' : `these ${String(added)} addresses`} again.`,
      })
      detail.refetch()
    })()
  }

  return (
    <div className="ar-stack ar-stack--loose">
      {/* No back link (parity P-LEAD-01): the rail's *Leads* entry is lit on
          this route and the breadcrumb names it. */}
      <div className="app-lead">
        <div className="ar-stack">
          <div className="ar-card">
            <div className="ar-card__header">
              {/* OUTR-21. `aria-hidden` because the name is the next thing read:
                  an avatar that announced "SI" before it would be the same two
                  letters twice, and initials are a guess at a person either way
                  (`lib/format.initials`). */}
              <span className="ar-avatar" aria-hidden="true">
                {initials(lead.full_name ?? '')}
              </span>
              <div className="app-lead__who">
                {/* OUTR-60. The page's `h1`, at the card-title size the design
                    draws, rather than an `h3` that would leave this screen with
                    no top-level heading at all. */}
                <h1 className="app-lead__name">{lead.full_name ?? 'Unnamed lead'}</h1>
                {/* *Title · Company · City* (P-LEAD-02). `company` holds the
                    headline when the capture could not split one from the
                    other, so a company equal to the headline is printed once. */}
                <div className="ar-subtle app-lead__sub">
                  {[
                    lead.headline,
                    lead.company === lead.headline ? null : lead.company,
                    lead.location,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'No title recorded'}
                </div>
              </div>
              <span className="ar-card__spacer" />
              <span className={`ar-badge ar-badge--${STAGE_TONES[lead.stage] ?? 'neutral'}`}>
                {STAGE_LABELS[lead.stage] ?? lead.stage}
              </span>
            </div>
            <div className="ar-card__body ar-stack">
              {contacts.length === 0 && data.profile_url === null ? (
                <Empty
                  icon="○"
                  title="No address for this lead"
                  body="Their post carried no email address or phone number, so nothing can be sent to them. They stay in the list because the post itself may still be worth reading."
                />
              ) : (
                <ContactColumns contacts={contacts} profileUrl={data.profile_url ?? null} />
              )}
              <span className="ar-field__help">
                {/* The mockup's sentence (P-LEAD-04), with *they* for *she*: the
                    pronoun in the drawing is its persona's, and the product does
                    not know a lead's. */}
                The email came out of the post below, where they published it themselves. We do not
                guess addresses and we do not buy lists.
              </span>
            </div>
          </div>

          <div className="ar-card">
            <div className="ar-card__header">
              <h2>The post we found them in</h2>
              <span className="ar-card__spacer" />
              {/* `12 Jul · 6 hours old when found` (P-LEAD-05). */}
              <span className="ar-badge ar-badge--neutral">
                {formatDayMonth(lead.first_seen_at, IST)}
                {postAge(data.posted_ago ?? null) === null
                  ? null
                  : ` · ${postAge(data.posted_ago ?? null) ?? ''} old when found`}
              </span>
            </div>
            <div className="ar-card__body ar-stack ar-stack--tight">
              {data.post_text === null ? (
                <Empty
                  icon="○"
                  title="No post text was kept"
                  body="This lead was added by hand or by an import rather than found in a search, so there is no post behind it."
                />
              ) : (
                <div className="ar-panel">
                  <p className="app-lead__post">{data.post_text}</p>
                </div>
              )}
              {/* OUTR-25 and OUTR-64. The keyword belongs under the post it
                  matched, as the chip the packs screen presses — in the card
                  header it read as a label on the card rather than as the reason
                  this post was ever read. */}
              <div className="ar-row app-lead__postmeta">
                {data.keyword === null ? null : (
                  <>
                    <span className="ar-subtle">Matched keyword</span>
                    <span className="app-chip" aria-pressed="true">
                      {data.keyword}
                    </span>
                  </>
                )}
                <span className="ar-card__spacer" />
                {data.post_url === null ? null : (
                  <a
                    className="ar-link"
                    href={data.post_url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Open on LinkedIn ↗
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="ar-card">
            <div className="ar-card__header">
              <h2>Activity</h2>
              <span className="ar-card__spacer" />
              <Link className="ar-link app-lead__meta" to="/outreach/compose">
                Edit the ladder
              </Link>
            </div>
            <ActivityList rows={activity} />
          </div>
        </div>

        <div className="ar-stack">
          <div className="ar-card">
            <div className="ar-card__header">
              <h2>Score {formatScore(lead.relevance_score ?? null)}</h2>
            </div>
            <div className="ar-card__body ar-stack ar-stack--tight">
              {/* A reason that is an error token — `ai-error`, what the search
                  stores when the model could not be reached — is not a
                  sentence anybody wrote, so it reads as unscored (P-LEAD-07). */}
              {lead.relevance_reason == null || isErrorToken(lead.relevance_reason) ? (
                <>
                  <p className="ar-subtle app-lead__meta">
                    Nobody has scored this lead. That happens when it was added by hand, or when the
                    model could not be reached during the search that found it — the lead is kept
                    either way, which is the direction {brand.productName} fails in on purpose.
                  </p>
                  {manageable ? (
                    <Button
                      tone="ghost"
                      size="sm"
                      busy={rescore.pending}
                      busyLabel="Scoring"
                      onClick={() => {
                        void rescore.run(undefined).then((result) => {
                          if (result.ok) detail.refetch()
                        })
                      }}
                    >
                      Score this lead now
                    </Button>
                  ) : null}
                  {rescore.state.status === 'error' ? (
                    <InlineError failure={rescore.state.failure} />
                  ) : null}
                </>
              ) : (
                <p className="app-lead__reason">{lead.relevance_reason}</p>
              )}
              <hr className="app-lead__rule" />
              <span className="ar-subtle app-lead__meta">
                Scored by Haiku 4.5 against the sentence in{' '}
                <Link className="ar-link" to="/outreach/compose">
                  what we say
                </Link>
                .
              </span>
            </div>
          </div>

          <div className="ar-card">
            <div className="ar-card__body ar-stack">
              <InlineError failure={move.state.failure ?? suppress.state.failure} />
              {data.conversation_id === null ? (
                <span className="ar-subtle app-lead__meta">
                  There is no conversation with this person yet. One opens the first time a message
                  is sent to them or arrives from them.
                </span>
              ) : (
                // OUTR-30. The id travels, so the inbox opens *this* thread
                // rather than the newest one the tenant happens to have.
                <Link
                  className="ar-btn ar-btn--primary ar-btn--block"
                  to={`/outreach/inbox?conversation=${String(data.conversation_id)}`}
                >
                  Open conversation
                </Link>
              )}
              {manageable ? (
                <>
                  {/* The mockup's three (P-LEAD-08): Open conversation (primary),
                      Move to Won (ghost), Add to suppression list (quiet).
                      *Mark as lost* and the list link are app extras and keep
                      their quiet tone below. */}
                  <Button
                    block
                    tone="ghost"
                    busy={move.pending}
                    disabled={lead.stage === 'won'}
                    onClick={() => {
                      onMove('won')
                    }}
                  >
                    Move to Won
                  </Button>
                  {/* OUTR-28 and OUTR-63. A block button rather than a link to a
                      screen where the address has to be typed again — and it is
                      absent, not disabled, for a lead with no address, because
                      there is nothing to suppress. */}
                  {blockable.length === 0 ? null : (
                    <Button block tone="quiet" busy={suppress.pending} onClick={onSuppress}>
                      Add to suppression list
                    </Button>
                  )}
                  <Button
                    block
                    tone="quiet"
                    busy={move.pending}
                    disabled={lead.stage === 'lost'}
                    onClick={() => {
                      onMove('lost')
                    }}
                  >
                    Mark as lost
                  </Button>
                </>
              ) : (
                <span className="ar-subtle app-lead__meta">
                  Only owners and admins can move a lead through the pipeline.
                </span>
              )}
              <Link className="ar-link app-lead__meta" to="/outreach/suppression">
                See your suppression list
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The three columns `outreach.html#lead` draws — Email · Phone · LinkedIn —
 * always, with a dash where the lead has none (parity P-LEAD-03). The email
 * and mobile come from the captured contacts; LinkedIn is the profile the lead
 * was found under. Any further contact kind is appended after the three.
 */
function ContactColumns({
  contacts,
  profileUrl,
}: {
  contacts: Contact[]
  profileUrl: string | null
}): JSX.Element {
  const email = contacts.find((contact) => contact.kind === 'email')
  const mobile = contacts.find((contact) => contact.kind === 'mobile')
  const linkedin = contacts.find((contact) => contact.kind === 'linkedin')
  const profile = linkedin === undefined && profileUrl !== null ? profileHref(profileUrl) : null
  const others = contacts.filter(
    (contact) => !['email', 'mobile', 'linkedin'].includes(contact.kind),
  )
  return (
    <div className="app-lead__contacts">
      <div>
        <div className="ar-muted app-lead__label">Email</div>
        {email ? (
          <ContactValue contact={email} />
        ) : (
          <div className="ar-mono app-lead__value">—</div>
        )}
        {email ? <Contacted contact={email} /> : null}
      </div>
      <div>
        <div className="ar-muted app-lead__label">Phone</div>
        {mobile ? (
          <ContactValue contact={mobile} />
        ) : (
          <div className="ar-mono app-lead__value">—</div>
        )}
        {mobile ? <Contacted contact={mobile} /> : null}
      </div>
      <div>
        <div className="ar-muted app-lead__label">LinkedIn</div>
        {linkedin ? (
          <ContactValue contact={linkedin} />
        ) : profile ? (
          <div className="app-lead__value">
            <a className="ar-link" href={profile.href} target="_blank" rel="noreferrer noopener">
              {profile.label}
            </a>
          </div>
        ) : (
          <div className="ar-mono app-lead__value">—</div>
        )}
        {linkedin ? <Contacted contact={linkedin} /> : null}
      </div>
      {others.map((contact) => (
        <div key={contact.id}>
          <div className="ar-muted app-lead__label">
            {CONTACT_LABELS[contact.kind] ?? contact.kind}
          </div>
          <ContactValue contact={contact} />
          <Contacted contact={contact} />
        </div>
      ))}
    </div>
  )
}

function Contacted({ contact }: { contact: Contact }): JSX.Element {
  return (
    <div className="ar-subtle app-lead__meta">
      {contact.first_contacted_at === null
        ? 'Never contacted'
        : `First contacted ${formatDateTime(contact.first_contacted_at ?? '', IST)}`}
    </div>
  )
}

/** `ai-error` and its kin: what the search stores as a reason when the model failed. */
function isErrorToken(reason: string): boolean {
  return /^[a-z-]*error[a-z-]*$/i.test(reason.trim())
}

/**
 * One address, and the LinkedIn one is a link — OUTR-23.
 *
 * The stored value is whatever the scrape captured, normalised only for case and
 * a trailing slash (`app/outreach/suppression.normalise`), so it may be a full
 * profile URL or something that is not a URL at all. Only an absolute `http(s)`
 * one is linked, and the path is what is shown: `/in/snehaiyer` is how a person
 * recognises a profile, and rendering `javascript:` as an anchor is how a scraped
 * string becomes a script.
 */
function ContactValue({ contact }: { contact: Contact }): JSX.Element {
  const href = contact.kind === 'linkedin' ? profileHref(contact.value) : null
  if (href === null) return <div className="ar-mono app-lead__value">{contact.value}</div>
  return (
    <div className="app-lead__value">
      <a className="ar-link" href={href.href} target="_blank" rel="noreferrer noopener">
        {href.label}
      </a>
    </div>
  )
}

function profileHref(value: string): { href: string; label: string } | null {
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    return null
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null
  return { href: parsed.href, label: parsed.pathname || parsed.href }
}

/**
 * Everything that has happened to this prospect, newest first — OUTR-26.
 *
 * The design draws *connection request sent · accepted · first touch sent,
 * opened twice · replied — interested*, and the merge behind it is the server's
 * (`app/v1/leads._activity`): the ladder answers what is scheduled and what
 * stopped, the messages answer what left and what came back. Neither alone is an
 * activity strip — the screen used to show only the first, so a prospect who had
 * replied twice and one who had never answered read identically.
 *
 * **Two of the prototype's four rows have no producer and are not invented.**
 * *Connection request sent* and *Accepted* are LinkedIn connection events, which
 * this product does not record, and *opened 4×* is a per-recipient read receipt,
 * which `messages` does not carry either. What is here is what happened.
 */
function ActivityList({ rows }: { rows: Activity[] }): JSX.Element {
  if (rows.length === 0)
    return (
      <div className="ar-card__body">
        <Empty
          icon="○"
          title="Nothing has happened yet"
          body="This lead is not in the follow-up ladder and nothing has been exchanged with them. Pressing Save & start sending on the What we say screen queues a first touch for everybody who has never been written to."
        />
      </div>
    )
  return (
    <div className="ar-card__body ar-stack ar-stack--tight">
      {rows.map((row, index) => (
        <div className="app-lead__event" key={`${row.kind}-${row.at}-${String(index)}`}>
          <div className="ar-row app-lead__step">
            <span className={`app-chan app-chan--${row.channel}`}>{row.channel}</span>
            <span className="app-lead__label">
              {row.kind === 'message_in' ? <strong>{row.label}</strong> : row.label}
            </span>
            <span className="ar-card__spacer" />
            <span className="ar-subtle app-lead__meta">{formatDateTime(row.at, IST)}</span>
          </div>
          {detailOf(row) === null ? null : (
            <span className="ar-subtle app-lead__meta">{detailOf(row)}</span>
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * The second line under an event, in the tenant's words rather than the model's.
 *
 * A cancelled rung says *why*, which is the question a tenant asks about a
 * prospect who went quiet, and `cancelled_reason` is the only column that can
 * answer it. A reply says what the classifier made of it, which is the label the
 * inbox lets them overrule.
 */
function detailOf(row: Activity): string | null {
  const detail = row.detail ?? null
  if (detail === null) return null
  if (row.kind === 'follow_up')
    return CANCEL_REASONS[detail] === undefined
      ? detail
      : `stopped because ${CANCEL_REASONS[detail]}`
  if (row.kind === 'message_in')
    return `Read as ${REPLY_CATEGORIES[detail] ?? detail}, which you can change in the inbox.`
  return detail.charAt(0).toUpperCase() + detail.slice(1)
}
